import React from 'react';
import i18next from 'i18next';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { loadRecommendations as loadRecommendationsAction } from 'redux/modules/recommended';

import Breakpoints from 'lib/Breakpoints';
import { recommendation as recommendationPropType } from 'lib/CustomPropTypes';
import { extractTermIds } from 'lib/taxonomy';
import recommendationOptions, { getUnibrowData } from 'lib/vilynxOptions';

import Related from 'components/Related';
import GiftGuide from 'components/GiftGuide';

const mapStateToProps = ({ recommended, shared }) => ({
  recos: recommended.recommendations,
  vertical: shared.vertical,
});

const mapActionsToProps = {
  loadRecommendations: loadRecommendationsAction,
};

class Recommended extends React.Component {
  static propTypes = {
    additionalClasses: PropTypes.string,
    isGiftGuide: PropTypes.bool,
    isNBCNewsCommerce: PropTypes.bool,
    isBetterNewsCommerce: PropTypes.bool,
    isRightRail: PropTypes.bool,
    isTrending: PropTypes.bool,
    loadRecommendations: PropTypes.func.isRequired,
    mobile: PropTypes.bool,
    recos: PropTypes.objectOf(
      PropTypes.arrayOf(recommendationPropType),
    ),
    taxonomy: PropTypes.shape({
      primarySection: PropTypes.shape({
        id: PropTypes.string,
      }),
      primaryTopic: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
    url: PropTypes.string,
    vertical: PropTypes.string.isRequired,
  };

  static defaultProps = {
    additionalClasses: '',
    isGiftGuide: false,
    isNBCNewsCommerce: false,
    isBetterNewsCommerce: false,
    isRightRail: false,
    isTrending: false,
    mobile: true,
    recos: {},
    taxonomy: {},
    url: '',
  };

  componentDidMount() {
    const {
      loadRecommendations,
      taxonomy,
      url,
      vertical,
    } = this.props;

    if (this.shouldShow()) {
      // Get configuration options for recommendations
      const options = recommendationOptions({
        options: {
          limit: this.getLimit(),
          callbacks: {
            // No filter
            filter: () => true,
            // Function to apply to every recommendation item
            map: (row) => ({
              ...row,
              id: row.url.replace(/^.*-([0-9]+)$/, 'mmvo$1'),
              url: row.url ? `https://${row.url}` : null,
              thumbnail: row.thumbnail ? `https://${row.thumbnail}` : null,
              unibrow: getUnibrowData(row),
            }),
          },
        },
        taxonomy: extractTermIds(taxonomy),
        type: this.getType(),
        vertical,
      });

      loadRecommendations(url, options, this.getType());
    }
  }

  getType = () => {
    const {
      isGiftGuide,
      isTrending,
      isNBCNewsCommerce,
      isBetterNewsCommerce,
    } = this.props;
    if (isTrending && !isBetterNewsCommerce) {
      return 'trending';
    }
    if (isGiftGuide) {
      return 'giftGuide';
    }
    if (isNBCNewsCommerce) {
      return 'nbcNewsCommerce';
    }
    if (isBetterNewsCommerce) {
      return 'betterNewsCommerce';
    }
    return 'article';
  }

  getLimit = () => {
    const { isGiftGuide, isTrending, isNBCNewsCommerce } = this.props;
    if (isTrending || isGiftGuide) {
      return 5;
    }
    if (isNBCNewsCommerce) {
      return 4;
    }
    return Breakpoints.isS() ? 1 : 2;
  }

  shouldShow = () => {
    const {
      mobile,
      isTrending,
      isGiftGuide,
      isNBCNewsCommerce,
      isBetterNewsCommerce,
    } = this.props;
    if (isTrending || isGiftGuide || isNBCNewsCommerce || isBetterNewsCommerce) {
      return true;
    }
    return (mobile && Breakpoints.isSorM()) || (!mobile && Breakpoints.isLorXL());
  }

  render() {
    const {
      additionalClasses,
      isGiftGuide,
      isNBCNewsCommerce,
      isBetterNewsCommerce,
      isRightRail,
      isTrending,
      recos,
    } = this.props;

    const items = recos[this.getType()] || undefined;

    // No results, return early
    if (!Array.isArray(items) || !items.length) {
      return null;
    }

    const title = isTrending ? i18next.t('Trending') : i18next.t('Recommended');
    const id = title.toLowerCase();

    let linkTrackingID;
    if (isTrending || isNBCNewsCommerce) {
      linkTrackingID = 'recommended_shop';
    } else {
      linkTrackingID = id;
    }

    if (isGiftGuide) {
      return (
        <GiftGuide
          items={items}
          data-test="gift-guide"
        />
      );
    }

    if (this.shouldShow()) {
      return (
        <Related
          additionalClasses={additionalClasses}
          elementId={id}
          isNBCNewsCommerce={isNBCNewsCommerce}
          isBetterNewsCommerce={isBetterNewsCommerce}
          isRightRail={isRightRail}
          isTrending={isTrending}
          items={items.slice(0, 4)}
          linkTracking={linkTrackingID}
          title={title}
        />
      );
    }

    return null;
  }
}

export default connect(mapStateToProps, mapActionsToProps)(Recommended);
